import React, { useState } from "react"

import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { CheckboxElement } from "@front-shared/elements/checkbox.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"

import { AUTH_CONFIG, LoginStep, MFAType } from "../../../../config/auth.config"
import { useAppDispatch } from "../../../../store/appDispatch.hook"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { authSelector } from "../../../../store/selectors/auth.selector"
import { authSetLoginStep } from "../../../../store/slices/auth.slice"

import checkCircleSolidIcon from "@front-shared/assets/images/icons/check-circle-solid.svg"

export const MfaSelectionStep: React.FunctionComponent = () => {
    const dispatch = useAppDispatch()
    const authState = useAppSelector(authSelector)

    const [enrollmentAccepted, setEnrollmentAccepted] = useState<boolean>()
    const [selectedMfaMethod, setSelectedMfaMethod] = useState<MFAType | null>(
        null,
    )

    const onContinue = () => {
        switch (selectedMfaMethod) {
            case MFAType.SMS:
                dispatch(authSetLoginStep(LoginStep.MFA_PHONE_COLLECTION))

                break
            case MFAType.TOTP:
                dispatch(authSetLoginStep(LoginStep.MFA_TOTP_SETUP))

                break
        }
    }

    return (
        <div className="relative">
            <PrimaryText
                className="whitespace-pre-wrap mb-[24px]"
                typography="h3"
            >
                Secure your account
            </PrimaryText>

            <PrimaryText
                className="whitespace-pre-wrap mb-[24px]"
                typography="text"
                size={20}
            >
                Set up multi-factor authentication (MFA) to keep your account
                safe.
            </PrimaryText>

            <PrimaryText
                typography="h5"
                className="mb-[24px]"
                weight="semibold"
            >
                Please select a method.
            </PrimaryText>

            <div className="flex gap-[24px] mb-[24px]">
                {AUTH_CONFIG.mfaSelectionOptions.map(
                    (selectionOption, index) => {
                        const isSelected =
                            selectedMfaMethod === selectionOption.value

                        return (
                            <button
                                key={index}
                                className={`flex-1 p-[24px] text-center border border-solid rounded-[6px] relative ${
                                    isSelected
                                        ? "bg-brand-white border-brand-primary ring-1 ring-brand-primary"
                                        : "border-brand-warmShadow"
                                }`}
                                onClick={() => {
                                    setSelectedMfaMethod(selectionOption.value)
                                }}
                            >
                                {!isSelected && (
                                    <div className="absolute w-[16px] h-[16px] top-[8px] left-[8px] rounded-full bg-brand-white border border-solid border-brand-warmShadow" />
                                )}

                                {isSelected && (
                                    <img
                                        src={checkCircleSolidIcon}
                                        alt="Check"
                                        className="absolute w-[16px] h-[16px] top-[8px] left-[8px]"
                                    />
                                )}

                                <PrimaryText
                                    typography="textBold"
                                    className="mb-[16px]"
                                >
                                    {selectionOption.title}
                                </PrimaryText>

                                <PrimaryText lineHeight={20}>
                                    {selectionOption.description}
                                </PrimaryText>
                            </button>
                        )
                    },
                )}
            </div>

            <div className="flex justify-between gap-[24px]">
                <CheckboxElement
                    checkboxClassName="!flex !items-start"
                    name="enrollmentAccepted"
                    onChange={setEnrollmentAccepted}
                    label={
                        <PrimaryText>
                            I agree to set up and use Multi-Factor
                            Authentication
                            <br />
                            (MFA) to protect my account.
                        </PrimaryText>
                    }
                />

                <ButtonElement
                    label="Continue"
                    type="primary"
                    htmlType="button"
                    size="large"
                    isLoading={authState.isLoading}
                    disabled={!enrollmentAccepted || !selectedMfaMethod}
                    onClick={onContinue}
                />
            </div>
        </div>
    )
}
